import { getBinaryMarketOptions, getBinaryProbabilityPercentage } from './binary-options';
import { InteractWatchIcon } from '@/client/assets';
import { ApiTranslation } from '@/client/components/api-translation';
import { CurrencyView } from '@/client/components/currency-view';
import { FollowMarket } from '@/client/components/follow-market';
import { LocalizedText } from '@/client/components/localized-text';
import { MarketDeadline } from '@/client/components/market-deadline';
import { OptionButton } from '@/client/components/option-button';
import { ShareMarket } from '@/client/components/share-market';
import { TextTooltip } from '@/client/components/text-tooltip';
import { UploadedImage } from '@/client/components/uploaded-image';
import { prefetchRoute } from '@/client/confs/page-loaders';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { queryClient } from '@/client/utils/query-client';
import { buildEventDetailQueryOptions } from '@/libs/react-sdk/api/use-event-detail';
import { FollowStatus, Market, MarketStatus } from '@/libs/sdk/api';
import useInViewport from 'ahooks/lib/useInViewport';
import { memo, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

// Category emoji mapping for fallback
const getCategoryEmoji = (category?: string): string => {
  const emojiMap: Record<string, string> = {
    crypto: '₿',
    politics: '🏛️',
    sports: '⚽',
    tech: '💻',
    entertainment: '🎬',
    economy: '📊',
    science: '🔬',
    culture: '🎭',
    world: '🌍',
    finance: '💰',
  };
  return emojiMap[category?.toLowerCase() || ''] || '📈';
};

// Moved to components/page-skeletons so route fallbacks can use it without
// pulling this module's data graph into the entry bundle; re-exported here to
// keep existing import sites working.
export { MarketCardSkeleton } from '@/client/components/page-skeletons/market-card-skeleton';

type MarketCardProps = {
  className?: string;
  market: Market;
  index?: number;
};

const MarketCardComponent = ({ className, market, index = 0 }: MarketCardProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const href = `/event/${market.eventId}`;
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // Element state (not a ref) so the observer re-attaches when the node mounts
  // late — with a plain ref, a card that first renders null (options not yet
  // loaded) is never observed and stays at opacity 0 forever.
  const [cardEl, setCardEl] = useState<HTMLDivElement | null>(null);

  // Intersection observer for fade-in animation
  const [inViewport] = useInViewport(cardEl ?? undefined, {
    threshold: 0.1,
  });

  useEffect(() => {
    if (inViewport && !isVisible) {
      // Stagger delay based on index (max 8 items per row * 50ms)
      const delay = Math.min(index % 8, 7) * 50;
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [inViewport, isVisible, index]);

  // Belt-and-braces reveal for any remaining observer miss: rect-check when
  // the element mounts (cardEl is state, so a late mount re-fires this).
  useEffect(() => {
    if (isVisible) return;
    const el = cardEl;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      const delay = Math.min(index % 8, 7) * 50;
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    }
  }, [cardEl, isVisible, index]);

  const { yes, no } = useMemo(() => getBinaryMarketOptions(market.options), [market.options]);
  // Fallback option list for PredictionCTF contracts where on-chain options() reverts
  const apiOptionList = useMemo(() => market.options.map(o => ({ optionAddress: o.tokenAddress })), [market.options]);
  const resultOption = useMemo(
    () => market?.options.find(item => item.tokenAddress === market?.result),
    [market?.options, market?.result]
  );

  // Calculate probability percentage for the progress bar
  const probability = useMemo(() => {
    return getBinaryProbabilityPercentage(market.options) ?? 50;
  }, [market.options]);

  if (!yes || !no) return null;

  // Warm the event route chunk + event-detail cache on intent signals so the
  // click-through transition commits without suspending.
  const handlePrefetch = () => {
    prefetchRoute('event');
    if (market.eventId) void queryClient.prefetchQuery(buildEventDetailQueryOptions(api, { eventId: market.eventId }));
  };

  return (
    <div
      ref={setCardEl}
      className={cn(
        // mcard-neon: animated diagonal streaks + sheen sweep + neon hover (design handoff)
        // mcard-surface: translucent gold-tinted "inside colour" gradient
        'mcard-neon mcard-surface group p-5 w-full flex flex-col gap-3.5 cursor-pointer',
        'rounded-2xl',
        'transition-all duration-300 ease-out',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
        isVisible && 'mcard-reveal-glow in',
        className
      )}
      onClick={() => navigate(href)}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      onTouchStart={handlePrefetch}
    >
      {/* Header with image, category chip and title */}
      <div className="flex items-start gap-3">
        {/* Image with fallback */}
        <div className="shrink-0 size-10 rounded-lg overflow-hidden bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center relative border border-border">
          {market.marketPicUrl && !imageError ? (
            <UploadedImage
              fileKey={market.marketPicUrl}
              className="size-full object-cover absolute inset-0"
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="text-base">{getCategoryEmoji(market.categoryName)}</span>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          {market.categoryName && (
            <span className="neon-chip cat">
              <ApiTranslation>{market.categoryName}</ApiTranslation>
            </span>
          )}
          <TextTooltip tooltip={market.marketName} className="neon-q line-clamp-2">
            <LocalizedText i18nMap={market.nameI18n} fallback={market.marketName} />
          </TextTooltip>
        </div>
      </div>

      {/* Trading section for active markets */}
      {market.marketStatus == MarketStatus.progress && (
        <div className="space-y-3">
          {/* Probability % + gold progress bar */}
          <div className="neon-prob">
            <div className="pct" style={{ color: probability >= 50 ? 'var(--neon-yes)' : 'var(--neon-no)' }}>
              {probability}%<small>{t('chance')?.toUpperCase()}</small>
            </div>
            <div className="neon-bar">
              <i style={{ width: `${probability}%` }} />
            </div>
          </div>

          {/* Yes/No buttons */}
          <div className="flex gap-2">
            <OptionButton
              marketAddress={market.marketAddress}
              marketDecimal={yes.decimal}
              optionAddress={yes.tokenAddress}
              optionName={yes.tokenName}
              optionDescription={yes.tokenDescription}
              optionPrice={Number(yes.tokenPrice)}
              optionDecimal={yes.decimal}
              optionSide="yes"
              apiOptionList={apiOptionList}
              navigateTo={href}
              className="flex-1 h-8 rounded-md font-medium text-sm"
            >
              <ApiTranslation>{yes.tokenDescription || 'Yes'}</ApiTranslation>
            </OptionButton>
            <OptionButton
              marketAddress={market.marketAddress}
              marketDecimal={no.decimal}
              optionAddress={no.tokenAddress}
              optionName={no.tokenName}
              optionDescription={no.tokenDescription}
              optionPrice={Number(no.tokenPrice)}
              optionDecimal={no.decimal}
              optionSide="no"
              apiOptionList={apiOptionList}
              navigateTo={href}
              className="flex-1 h-8 rounded-md font-medium text-sm"
            >
              <ApiTranslation>{no.tokenDescription || 'No'}</ApiTranslation>
            </OptionButton>
          </div>
        </div>
      )}

      {/* Result section for settled markets */}
      {market.marketStatus >= MarketStatus.settlement && resultOption && (
        <div
          className={cn(
            'mt-3 text-xs font-medium py-1.5 px-2 rounded-md',
            resultOption.tokenAddress === yes.tokenAddress
              ? 'text-emerald-600 bg-emerald-500/10'
              : 'text-red-500 bg-red-500/10'
          )}
        >
          {t('results-out-label')} {resultOption.tokenDescription}
        </div>
      )}

      {/* Footer with volume, date and actions */}
      <div className="neon-meta mt-auto pt-3">
        <CurrencyView
          className="font-mono whitespace-nowrap"
          value={market.marketVolume}
          decimal={market.marketDecimal}
          baseTokenAddress={market.baseTokenAddress}
          showIcon
          showUnit
          unitClassName="text-[10px]"
        >
          {t('vol')}
        </CurrencyView>
        <div className="flex items-center gap-2 ml-auto">
          <MarketDeadline deadline={Number(market.deadline)} status={market.marketStatus} className="text-[11px]" />
          <FollowMarket
            variant="ghost"
            iconClassName="p-0"
            className="size-3.5 hover:text-foreground transition-colors"
            watchIcon={<InteractWatchIcon className="size-3.5 fill-current" />}
            isFollowed={market.isFollowed == FollowStatus.yes}
            address={market.marketAddress}
          />
          <ShareMarket
            variant="ghost"
            className="size-3.5 hover:text-foreground transition-colors"
            iconClassName="p-0"
            marketName={market.marketName}
            marketAddress={market.marketAddress}
          />
        </div>
      </div>
    </div>
  );
};

export const MarketCard = memo(MarketCardComponent);
MarketCard.displayName = 'MarketCard';
